/* eslint-disable import/no-default-export */
import { HeaderTestIds } from '@trello/webdriverio-tests';
import { dynamicDebounce } from '@trello/time';
import classNames from 'classnames';
import { Icon, IconColor, IconName } from 'app/common/components/controls';
import { Noun, Verb, trackUe } from '@trello/analytics';
import HeaderLink from 'app/gamma/src/components/header/header-link';
import { ScreenBreakpoints } from 'app/src/components/Responsive';
import { getKey, Key, Scope, useShortcut } from '@trello/keybindings';
import RouterLink from 'app/common/components/singlespa/router-link';
import {
  clearSearchQuery,
  clearSearchSuggestions,
  getSearchSuggestions,
  performSearch,
  setSearchQuery,
  setSelectedSuggestion,
} from 'app/gamma/src/modules/state/models/search';
import { State } from 'app/gamma/src/modules/types';
import {
  hideAddSavedSearchForm,
  hideSavedSearchPromo,
} from 'app/gamma/src/modules/state/ui/search';
import React, {
  lazy,
  Suspense,
  FunctionComponent,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import Media from 'react-media';
import { connect } from 'react-redux';
import { Dispatch } from 'app/gamma/src/types';
import { getBoardsByIds } from 'app/gamma/src/selectors/boards';
import { getMembersByIds } from 'app/gamma/src/selectors/members';
import {
  getCurrentSearchQuery,
  getSearchSuggestions as selectSearchSuggestions,
  getSelectedSuggestion,
  getLastSearchQuery,
} from 'app/gamma/src/selectors/search';
import styles from './form.less';
import {
  BoardModel,
  MemberModel,
  SearchSuggestionType as SuggestionType,
} from 'app/gamma/src/types/models';
import { Analytics } from '@trello/atlassian-analytics';
import { featureFlagClient, useFeatureFlag } from '@trello/feature-flag-client';
import { Popover, usePopover } from '@trello/nachos';

const SearchResultsPopoverPromise = import(
  /* webpackChunkName: "search-results-popover" */ 'app/gamma/src/components/search-results'
);
const SearchResultsPopover = lazy(() => SearchResultsPopoverPromise);

const alphabet: string =
  '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

const randomString = (length: number): string => {
  const letters: string[] = [];

  while (letters.length < length) {
    letters.push(alphabet[Math.floor(Math.random() * alphabet.length)]);
  }

  return letters.join('');
};

const inputId = randomString(32);

interface StateProps {
  currentQuery: string;
  lastQuery: string;
  allSuggestions: (SuggestionType | BoardModel | MemberModel)[];
  keywords: SuggestionType[];
  boards: BoardModel[];
  members: MemberModel[];
  selectedSuggestion: string;
  brandingLogo: string | undefined;
  brandingName: string | undefined;
}

interface DispatchProps {
  clearSearchInput: () => void;
  onInputBlur: (hasQuery: boolean) => void;
  onInputClick: () => void;
  onSearch: (query: string) => void;
  onSuggest: (query: string, caretPos: number) => void;
  onSetSearchQuery: (query: string) => void;
  onSearchSuggestion: (query: string) => void;
  onSetSelectedSuggestion: (selected: string | null) => void;
}

interface AllProps extends StateProps, DispatchProps {}

function usePrevious<T>(value: T) {
  const ref = useRef<T>();

  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]);

  // Return previous value (happens before update in useEffect above)
  return ref.current;
}

const SearchForm: FunctionComponent<AllProps> = ({
  currentQuery,
  lastQuery,
  allSuggestions,
  keywords,
  boards,
  members,
  selectedSuggestion,
  brandingLogo,
  brandingName,
  clearSearchInput,
  onInputBlur,
  onInputClick,
  onSearch,
  onSuggest,
  onSetSearchQuery,
  onSearchSuggestion,
  onSetSelectedSuggestion,
}) => {
  const { triggerRef, show, popoverProps, hide } = usePopover<
    HTMLInputElement
  >();
  const { isVisible, triggerElement } = popoverProps;

  const debouncedSearch = useMemo(() => {
    return dynamicDebounce(onSearch, 1500);
  }, [onSearch]);

  const debouncedSuggest = useMemo(() => dynamicDebounce(onSuggest, 100), [
    onSuggest,
  ]);

  const sendGASEvent = useFeatureFlag('dataeng.gasv3-event-tracking', false);

  /**
   * Because the search results popover is shown/hidden in response to the
   * focus and blur events and because those events are dispatched before
   * click events, we need to call preventDefault() to keep the popover from
   * closing itself when the input is clicked.
   */
  const stopClickPropagation = useCallback(
    (e: MouseEvent) => {
      if (e.target === triggerElement) {
        e.preventDefault();
      }
    },
    [triggerElement],
  );
  useEffect(() => {
    document.addEventListener('click', stopClickPropagation, true);
    return () => {
      document.removeEventListener('click', stopClickPropagation, true);
    };
  }, [stopClickPropagation]);

  const onShortcut = useCallback(() => {
    show();

    trackUe({
      category: Noun.HEADER,
      verb: Verb.OPENS,
      directObj: Noun.SEARCH_INPUT,
      method: 'by using search shortcut',
    });

    if (sendGASEvent) {
      Analytics.sendUIEvent({
        action: 'pressed',
        actionSubject: 'shortcut',
        actionSubjectId: 'searchShortcut',
        source: 'appHeader',
        attributes: {
          keyValue: '/',
        },
      });
    }
  }, [sendGASEvent, show]);
  useShortcut(onShortcut, {
    scope: Scope.Global,
    key: Key.ForwardSlash,
  });

  // This is for when `onInputBlur` gets called without a MouseEvent
  // (namely when the user presses `Esc`). Without this, the popover closes,
  // but the input remains in the active state
  const prevIsVisible = usePrevious(isVisible);
  const prevCurrentQuery = usePrevious(currentQuery);
  useEffect(() => {
    if (triggerElement && !isVisible && prevIsVisible) {
      triggerElement.blur();
      clearSearchInput();
    }

    // Also needed for focus without mouse event
    if (
      triggerElement &&
      isVisible &&
      (!prevIsVisible ||
        currentQuery === '' ||
        currentQuery !== prevCurrentQuery)
    ) {
      triggerElement.focus();
    }
  }, [
    clearSearchInput,
    currentQuery,
    isVisible,
    prevCurrentQuery,
    prevIsVisible,
    triggerElement,
  ]);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.currentTarget.value;
      onSetSearchQuery(query);
      if (query.length) {
        debouncedSearch(query);
        debouncedSuggest(
          query,
          triggerElement && triggerElement.selectionStart
            ? triggerElement.selectionStart
            : 0,
        );
      }
    },
    [debouncedSearch, debouncedSuggest, onSetSearchQuery, triggerElement],
  );

  const onBlur = useCallback(() => {
    const hasQuery = !!currentQuery;
    clearSearchInput();
    hide();

    // prevents calling this twice when pressing escape
    if (isVisible) {
      onInputBlur(hasQuery);
    }
  }, [clearSearchInput, currentQuery, hide, onInputBlur, isVisible]);

  const onMouseDown = useCallback(() => {
    if (!isVisible) {
      onInputClick();
    }
  }, [onInputClick, isVisible]);

  const getSelectedIndex = useCallback(() => {
    let index = -1;
    if (keywords.length) {
      index = keywords.findIndex((keyword) => keyword === selectedSuggestion);
    }
    if (index === -1 && (boards.length || members.length)) {
      index = [...boards, ...members].findIndex(
        (suggestion) => suggestion.id === selectedSuggestion,
      );
    }

    return index;
  }, [boards, keywords, members, selectedSuggestion]);

  const getInitalQuery = useCallback(() => {
    return currentQuery.substring(0, currentQuery.lastIndexOf(' ') + 1);
  }, [currentQuery]);

  const onBoardSuggestion = useCallback(
    (board: BoardModel) => {
      onSearchSuggestion(`${getInitalQuery()}board:"${board.name}" `);
    },
    [getInitalQuery, onSearchSuggestion],
  );

  const onMemberSuggestion = useCallback(
    (member: MemberModel) => {
      onSearchSuggestion(`${getInitalQuery()}@${member.username} `);
    },
    [getInitalQuery, onSearchSuggestion],
  );

  const onKeywordSuggestion = useCallback(
    (keyword: string) => {
      onSearchSuggestion(`${getInitalQuery()}${keyword} `);
    },
    [getInitalQuery, onSearchSuggestion],
  );

  const onUpArrow = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (!allSuggestions.length) {
        return;
      }
      e.preventDefault();
      const index = getSelectedIndex();
      const next: number = index > 0 ? index - 1 : allSuggestions.length - 1;
      const nextSuggestion = allSuggestions[next];
      onSetSelectedSuggestion(
        // @ts-ignore
        nextSuggestion.id ? nextSuggestion.id : nextSuggestion,
      );
    },
    [allSuggestions, getSelectedIndex, onSetSelectedSuggestion],
  );

  const onDownArrow = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (!allSuggestions.length) {
        return;
      }
      e.preventDefault();
      const index = getSelectedIndex();
      const length: number = allSuggestions.length - 1;
      const next: number = index === length ? 0 : index + 1;
      const nextSuggestion = allSuggestions[next];
      onSetSelectedSuggestion(
        // @ts-ignore
        nextSuggestion.id ? nextSuggestion.id : nextSuggestion,
      );
    },
    [allSuggestions, getSelectedIndex, onSetSelectedSuggestion],
  );

  const onEnter = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (!allSuggestions.length) {
        return;
      }
      e.preventDefault();
      const index = getSelectedIndex();
      if (index < keywords.length) {
        onKeywordSuggestion(keywords[index]);
      } else if (
        index >= keywords.length &&
        index < keywords.length + boards.length
      ) {
        onBoardSuggestion(boards[index - keywords.length]);
      } else {
        onMemberSuggestion(members[index - (keywords.length + boards.length)]);
      }
    },
    [
      allSuggestions.length,
      boards,
      getSelectedIndex,
      keywords,
      members,
      onBoardSuggestion,
      onKeywordSuggestion,
      onMemberSuggestion,
    ],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      switch (getKey(e)) {
        case Key.Escape:
          onBlur();
          e.preventDefault();
          e.stopPropagation();
          break;

        case Key.ArrowUp:
          onUpArrow(e);
          break;

        case Key.ArrowDown:
          onDownArrow(e);
          break;

        case Key.Enter:
        case Key.Tab:
          onEnter(e);
          break;

        default:
      }
    },
    [onBlur, onDownArrow, onEnter, onUpArrow],
  );

  return (
    <>
      <Suspense fallback={null}>
        <Popover
          {...popoverProps}
          testId={HeaderTestIds.SearchPopover}
          dangerous_disableFocusManagement
          dangerous_className={styles.popoverContent}
          dangerous_width={600}
        >
          <SearchResultsPopover inputElement={triggerElement} />
        </Popover>
      </Suspense>
      <Media
        queries={{
          small: ScreenBreakpoints.Small,
          medium: ScreenBreakpoints.Medium,
        }}
      >
        {(matches: { small: boolean; medium: boolean }) =>
          matches.small || (matches.medium && brandingName) ? (
            <HeaderLink href="/search" buttonStyle="button">
              <Icon name={IconName.Search} color={IconColor.Light} medium />
            </HeaderLink>
          ) : (
            <div
              className={classNames(
                styles.search,
                isVisible && styles.active,
                brandingLogo && styles.branded,
              )}
            >
              <input
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                className={styles.searchInput}
                data-test-id={HeaderTestIds.SearchInput}
                id={inputId}
                onMouseDown={onMouseDown}
                onFocus={show}
                onKeyDown={onKeyDown}
                onChange={onChange}
                ref={triggerRef}
                type="search"
                value={currentQuery}
              />
              <span className={styles.buttons}>
                {isVisible ? (
                  <>
                    <RouterLink
                      href={`/search?q=${encodeURIComponent(lastQuery)}`}
                    >
                      <Icon
                        className={classNames([
                          styles.button,
                          styles.externalIconButton,
                        ])}
                        medium
                        name={IconName.ExternalLink}
                      />
                    </RouterLink>
                    <Icon
                      className={styles.button}
                      medium
                      name={IconName.Close}
                      testId={HeaderTestIds.SearchClose}
                    />
                  </>
                ) : (
                  <label htmlFor={inputId}>
                    <Icon
                      className={styles.button}
                      medium
                      name={IconName.Search}
                    />
                  </label>
                )}
              </span>
            </div>
          )
        }
      </Media>
    </>
  );
};

const mapStateToProps = (state: State): StateProps => {
  const { keywords, idMembers, idBoards } = selectSearchSuggestions(state);
  const boards: BoardModel[] = getBoardsByIds(state, idBoards);
  const members: MemberModel[] = getMembersByIds(state, idMembers);
  const selectedSuggestion = getSelectedSuggestion(state);
  const allSuggestions = [...keywords, ...boards, ...members];
  const currentQuery = getCurrentSearchQuery(state);
  const lastQuery = getLastSearchQuery(state);

  return {
    brandingLogo: state.ui.header.brandingLogo,
    brandingName: state.ui.header.brandingName,
    allSuggestions,
    currentQuery,
    lastQuery,
    keywords,
    boards,
    members,
    selectedSuggestion,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  clearSearchInput() {
    dispatch(clearSearchQuery());
    dispatch(hideAddSavedSearchForm());
    dispatch(hideSavedSearchPromo());
  },
  onInputClick() {
    trackUe({
      category: Noun.HEADER,
      verb: Verb.OPENS,
      directObj: Noun.SEARCH_INPUT,
      method: 'by clicking on search input',
    });

    if (featureFlagClient.get('dataeng.gasv3-event-tracking', false)) {
      Analytics.sendUIEvent({
        action: 'clicked',
        actionSubject: 'input',
        actionSubjectId: 'searchInput',
        source: 'appHeader',
      });
    }
  },

  onSearch(query: string) {
    dispatch(performSearch({}));
  },

  onSuggest(query: string, caretPos: number) {
    dispatch(getSearchSuggestions(caretPos));
  },

  onSetSearchQuery(query: string) {
    dispatch(setSearchQuery({ query }));
  },

  onInputBlur(hasQuery: boolean) {
    dispatch(hideAddSavedSearchForm());
    dispatch(hideSavedSearchPromo());

    trackUe({
      category: Noun.HEADER,
      verb: Verb.CLOSES,
      directObj: Noun.SEARCH_INPUT,
      context: {
        hasQuery,
      },
    });

    if (featureFlagClient.get('dataeng.gasv3-event-tracking', false)) {
      Analytics.sendUIEvent({
        action: 'closed',
        actionSubject: 'inlineDialog',
        actionSubjectId: 'searchInlineDialog',
        source: 'appHeader',
        attributes: {
          hasQuery,
        },
      });
    }
  },
  onSearchSuggestion(query: string) {
    dispatch(clearSearchSuggestions());
    dispatch(setSearchQuery({ query }));
    dispatch(performSearch({}));
  },
  onSetSelectedSuggestion(selected: string | null) {
    dispatch(setSelectedSuggestion(selected));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SearchForm);
